import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAccess } from '../lib/access';
import { useWorkflow } from '../WorkflowContext';
import { useHome, channelLabel } from '../HomeContext';
import Tag from '../components/Tag';
import AttentionBoard from '../components/AttentionBoard';

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

function Widget({ title, sub, action, children }: { title: string; sub?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="card">
      <div className="card-header">
        <div><h3>{title}</h3>{sub && <p>{sub}</p>}</div>
        {action}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

export default function Home() {
  const { user, demo } = useAuth();
  const { canBackend } = useAccess();
  const nav = useNavigate();
  const wf = useWorkflow();
  const home = useHome();

  const me = (user?.displayName || (demo ? 'there' : 'there')).split(' ')[0];
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  const open = useMemo(() => wf.tasks.filter((t) => t.status !== 'completed'), [wf.tasks]);
  const inProgress = wf.tasks.filter((t) => t.status === 'in_progress' || t.status === 'acknowledged');
  const pending = wf.tasks.filter((t) => t.status === 'assigned' || t.status === 'blocked');
  const deadlines = open;
  const unreadComms = home.comms.filter((c) => c.unread).length;
  const unreadNotifs = home.notifications.filter((n) => !n.read).length;
  const openReminders = home.reminders.filter((r) => !r.done).length;

  const go = (path: string) => { if (canBackend) nav(path); };

  return (
    <>
      <div className="home-hero">
        <div>
          <div className="home-greet">{greeting()}, {me}.</div>
          <div className="page-sub">{today} · here is everything that needs you across your clients today.</div>
        </div>
        {canBackend
          ? <button className="btn primary backend-cta" onClick={() => nav('/dashboard')}>⚙ Open Staff CRM Backend</button>
          : <span className="demo-flag">Backend access restricted</span>}
      </div>

      <div className="grid cols-4" style={{ margin: '18px 0' }}>
        <div className="metric-card"><div className="metric-label">Open Tasks</div><div className="metric-value" style={{ color: 'var(--ac)' }}>{open.length}</div><div className="metric-note">Across your clients</div></div>
        <div className="metric-card"><div className="metric-label">Pending</div><div className="metric-value" style={{ color: 'var(--yl)' }}>{pending.length}</div><div className="metric-note">Awaiting ack or blocked</div></div>
        <div className="metric-card"><div className="metric-label">Deadlines</div><div className="metric-value" style={{ color: 'var(--or)' }}>{deadlines.length}</div><div className="metric-note">Upcoming commitments</div></div>
        <div className="metric-card"><div className="metric-label">Unread</div><div className="metric-value" style={{ color: 'var(--rd)' }}>{unreadComms + unreadNotifs}</div><div className="metric-note">{unreadComms} messages · {unreadNotifs} alerts</div></div>
      </div>

      <AttentionBoard />

      <div className="grid cols-3">
        <Widget title="Client Tasks" sub="Open work across your clients" action={<button className="btn small" onClick={() => go('/tasks')}>Board →</button>}>
          <div className="mini-list">
            {open.length === 0 && <div className="sub">No open tasks.</div>}
            {open.slice(0, 6).map((t) => (
              <div className="mini-row" key={t.id} onClick={() => go('/tasks')} style={{ cursor: canBackend ? 'pointer' : 'default' }}>
                <div className="mr-main"><strong>{t.title}</strong><div className="mr-sub">{t.client} · due {t.due}</div></div>
                <Tag kind="st" value={t.status} />
              </div>
            ))}
          </div>
        </Widget>

        <Widget title="Communications" sub={`${unreadComms} unread`} action={<button className="btn small" onClick={home.markAllCommsRead}>Mark all read</button>}>
          <div className="mini-list">
            {home.comms.map((c) => (
              <div className={`comm-row${c.unread ? ' unread' : ''}`} key={c.id} onClick={() => home.markCommRead(c.id)}>
                <span className={`chan-badge ch-${c.channel}`}>{channelLabel[c.channel]}</span>
                <div className="mr-main"><strong>{c.subject}</strong><div className="mr-sub">{c.client} · {c.preview}</div></div>
                <span className="comm-time">{c.time}</span>
              </div>
            ))}
          </div>
        </Widget>

        <Widget title="Requests" sub="Incoming client requests" action={<button className="btn small" onClick={() => go('/intake')}>Intake →</button>}>
          <div className="mini-list">
            {wf.intake.slice(0, 6).map((i) => (
              <div className="mini-row" key={i.id} onClick={() => go('/intake')} style={{ cursor: canBackend ? 'pointer' : 'default' }}>
                <div className="mr-main"><strong>{i.client}</strong><div className="mr-sub">{i.source} · {i.summary}</div></div>
                <Tag kind="st" value={i.status === 'Processed' ? 'completed' : 'new'} />
              </div>
            ))}
          </div>
        </Widget>
      </div>

      <div className="grid cols-3" style={{ marginTop: 16 }}>
        <Widget title="In Progress" sub="Actively being worked">
          <div className="mini-list">
            {inProgress.length === 0 && <div className="sub">Nothing in progress.</div>}
            {inProgress.map((t) => (
              <div className="mini-row" key={t.id}>
                <div className="mr-main"><strong>{t.title}</strong><div className="mr-sub">{t.client} · {t.owner}</div></div>
                <Tag kind="dp" value={t.dept} />
              </div>
            ))}
          </div>
        </Widget>

        <Widget title="Pending" sub="Awaiting acknowledgment or blocked">
          <div className="mini-list">
            {pending.length === 0 && <div className="sub">Nothing pending.</div>}
            {pending.map((t) => (
              <div className="mini-row" key={t.id}>
                <div className="mr-main"><strong>{t.title}</strong><div className="mr-sub">{t.client} · due {t.due}</div></div>
                <Tag kind="st" value={t.status} />
              </div>
            ))}
          </div>
        </Widget>

        <Widget title="Deadlines" sub="Upcoming client commitments">
          <div className="mini-list">
            {deadlines.length === 0 && <div className="sub">No upcoming deadlines.</div>}
            {deadlines.map((t) => (
              <div className="mini-row" key={t.id}>
                <div className="mr-main"><strong>{t.title}</strong><div className="mr-sub">{t.client}</div></div>
                <span className="due-chip">{t.due}</span>
              </div>
            ))}
          </div>
        </Widget>
      </div>

      <div className="grid cols-3" style={{ marginTop: 16 }}>
        <Widget title="Updates" sub="Recent synchronized activity" action={<button className="btn small" onClick={() => go('/audit')}>Audit →</button>}>
          <div className="mini-list">
            {wf.audit.slice(0, 6).map((a, idx) => (
              <div className="mini-row" key={idx}>
                <div className="mr-main"><strong>{a.title}</strong><div className="mr-sub">{a.detail}</div></div>
                <span className="comm-time">{a.time}</span>
              </div>
            ))}
          </div>
        </Widget>

        <Widget title="Notifications" sub={`${unreadNotifs} unread`} action={<button className="btn small" onClick={home.markAllNotifsRead}>Mark all read</button>}>
          <div className="mini-list">
            {home.notifications.map((n) => (
              <div className={`notif-row${n.read ? ' read' : ''}`} key={n.id} onClick={() => home.markNotifRead(n.id)}>
                <span className={`notif-dot nk-${n.kind}`} />
                <div className="mr-main"><strong>{n.text}</strong><div className="mr-sub">{n.time}</div></div>
              </div>
            ))}
          </div>
        </Widget>

        <Widget title="To-Dos & Reminders" sub={`${openReminders} open`}>
          <div className="mini-list">
            {home.reminders.map((r) => (
              <div className={`reminder-row${r.done ? ' done' : ''}`} key={r.id} onClick={() => home.toggleReminder(r.id)}>
                <span className="rm-check">{r.done ? '✓' : ''}</span>
                <div className="mr-main"><strong>{r.text}</strong><div className="mr-sub">{r.client} · {r.due}</div></div>
              </div>
            ))}
          </div>
        </Widget>
      </div>
    </>
  );
}
